import { Link, useLocation } from "react-router-dom";
import {
  ArrowRight,
  ChevronDown,
  Package,
  Printer,
  Sun,
  Palette,
  Layers,
  Layers3,
  Shirt,
  Scissors,
  Grid3x3,
  Flame,
  Sparkles,
  Droplets,
  Wrench,
  HeadphonesIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocalizedField } from "@/lib/i18n-db";

interface Category {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  image_url?: string | null;
}

interface MegaMenuPopoverProps {
  categories: Category[];
  type: "equipment" | "consumables";
  label: string;
}

// Icon mapping per slug
const ICON_MAP: Record<string, React.ElementType> = {
  // Equipment
  "impressora-dtf": Printer,
  "impressora-uv": Sun,
  "sublimacao": Palette,
  "impressora-ecosolvente": Layers,
  "textil-hibrida": Shirt,
  "laser-co2": Scissors,
  "mesa-corte-faca-cnc": Grid3x3,
  "prensas": Flame,
  "calandras": Flame,
  "laminadoras": Layers3,
  "bordados": Sparkles,
  "outros-equipamentos": Package,
  // Consumables
  "tinta-dtg": Droplets,
  "tintas-sublimacao": Droplets,
  "tintas-dtf": Droplets,
  "tintas-uv": Droplets,
  "tintas-eco-solvente": Droplets,
  "peliculas-de-impressao": Layers,
  "po-transferencia": Sparkles,
  "pecas-acessorios": Wrench,
  "textil": Shirt,
};

const getIcon = (slug: string) => ICON_MAP[slug] || Package;

// Group classification
const PRINTING_EQUIPMENT = new Set([
  "impressora-dtf",
  "impressora-uv",
  "sublimacao",
  "impressora-ecosolvente",
  "textil-hibrida",
]);

const INKS_CONSUMABLES = new Set([
  "tinta-dtg",
  "tintas-sublimacao",
  "tintas-dtf",
  "tintas-uv",
  "tintas-eco-solvente",
]);

const HIGHLIGHT_SLUGS = new Set(["impressora-dtf", "tintas-dtf"]);

type Accent = "primary" | "finishing";

// Accent palette per group, using existing semantic tokens
const ACCENT_STYLES: Record<Accent, {
  dot: string;
  title: string;
  highlightBg: string;
  iconChip: string;
  iconChipHover: string;
  textHover: string;
}> = {
  primary: {
    dot: "bg-[hsl(var(--equipment))]",
    title: "text-[hsl(var(--equipment))]",
    highlightBg: "bg-[hsl(var(--equipment-soft))] border border-[hsl(var(--equipment))]/25 hover:bg-[hsl(var(--equipment-soft))]/80",
    iconChip: "bg-[hsl(var(--equipment))] text-[hsl(var(--equipment-foreground))] shadow-sm",
    iconChipHover: "bg-muted text-muted-foreground group-hover:bg-[hsl(var(--equipment))]/10 group-hover:text-[hsl(var(--equipment))]",
    textHover: "group-hover:text-[hsl(var(--equipment))]",
  },
  finishing: {
    dot: "bg-[hsl(var(--consumables))]",
    title: "text-[hsl(var(--consumables))]",
    highlightBg: "bg-[hsl(var(--consumables-soft))] border border-[hsl(var(--consumables))]/25 hover:bg-[hsl(var(--consumables-soft))]/80",
    iconChip: "bg-[hsl(var(--consumables))] text-[hsl(var(--consumables-foreground))] shadow-sm",
    iconChipHover: "bg-muted text-muted-foreground group-hover:bg-[hsl(var(--consumables))]/10 group-hover:text-[hsl(var(--consumables))]",
    textHover: "group-hover:text-[hsl(var(--consumables))]",
  },
};

export function MegaMenuPopover({ categories, type, label }: MegaMenuPopoverProps) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const lf = useLocalizedField();
  const location = useLocation();
  const basePath = type === "equipment" ? "/equipamentos" : "/consumiveis";

  const handleLinkClick = () => setOpen(false);

  const { primaryGroup, secondaryGroup, primaryTitle, secondaryTitle, headerTitle, headerSubtitle } = useMemo(() => {
    // Priority order by buying intent / search volume (DTF always first)
    const INTENT_ORDER: Record<string, number> = {
      // Equipment — printing
      "impressora-dtf": 0,
      "impressora-uv": 1,
      "sublimacao": 2,
      "impressora-ecosolvente": 3,
      "textil-hibrida": 4,
      // Equipment — finishing
      "laser-co2": 0,
      "prensas": 1,
      "calandras": 2,
      "mesa-corte-faca-cnc": 3,
      "laminadoras": 4,
      "bordados": 5,
      "outros-equipamentos": 99,
      // Consumables — inks
      "tintas-dtf": 0,
      "tintas-sublimacao": 1,
      "tintas-uv": 2,
      "tintas-eco-solvente": 3,
      "tinta-dtg": 4,
      // Consumables — materials
      "peliculas-de-impressao": 0,
      "po-transferencia": 1,
      "textil": 2,
      "pecas-acessorios": 3,
    };
    const byIntent = (a: Category, b: Category) =>
      (INTENT_ORDER[a.slug] ?? 50) - (INTENT_ORDER[b.slug] ?? 50);

    if (type === "equipment") {
      const primary = categories.filter((c) => PRINTING_EQUIPMENT.has(c.slug)).sort(byIntent);
      const secondary = categories.filter((c) => !PRINTING_EQUIPMENT.has(c.slug)).sort(byIntent);
      return {
        primaryGroup: primary,
        secondaryGroup: secondary,
        primaryTitle: t("megaMenu.groupPrinting", "Sistemas de Impressão"),
        secondaryTitle: t("megaMenu.groupFinishing", "Corte & Acabamento"),
        headerTitle: t("megaMenu.headerEquipment", "Catálogo de Equipamentos"),
        headerSubtitle: t("megaMenu.subtitleEquipment", "Soluções industriais de alta performance"),
      };
    }
    const primary = categories.filter((c) => INKS_CONSUMABLES.has(c.slug)).sort(byIntent);
    const secondary = categories.filter((c) => !INKS_CONSUMABLES.has(c.slug)).sort(byIntent);
    return {
      primaryGroup: primary,
      secondaryGroup: secondary,
      primaryTitle: t("megaMenu.groupInks", "Tintas Profissionais"),
      secondaryTitle: t("megaMenu.groupMaterials", "Materiais & Acessórios"),
      headerTitle: t("megaMenu.headerConsumables", "Catálogo de Consumíveis"),
      headerSubtitle: t("megaMenu.subtitleConsumables", "Tintas, filmes e acessórios certificados"),
    };
  }, [categories, type, t]);

  const renderItem = (accent: Accent) => (category: Category) => {
    const Icon = getIcon(category.slug);
    const isHighlight = HIGHLIGHT_SLUGS.has(category.slug);
    const name = lf(category as any, "name") || category.name;
    const styles = ACCENT_STYLES[accent];
    const href = `${basePath}/${category.slug}`;
    const isActive = location.pathname === href || location.pathname.startsWith(`${href}/`);
    return (
      <Link
        key={category.id}
        to={href}
        onClick={handleLinkClick}
        aria-current={isActive ? "page" : undefined}
        className={cn(
          "group flex items-center gap-3 px-2.5 py-2 rounded-md transition-colors",
          isActive ? styles.highlightBg : "hover:bg-accent"
        )}
      >
        <span className={cn("flex-shrink-0 flex h-8 w-8 items-center justify-center rounded-md transition-colors",
          isHighlight || isActive ? styles.iconChip : styles.iconChipHover)}>
          <Icon className="h-4 w-4" strokeWidth={1.75} />
        </span>
        <span className={cn("text-sm font-medium text-foreground truncate transition-colors", styles.textHover)}>{name}</span>
        {isHighlight && (
          <Badge variant="secondary" className="ml-auto h-5 px-1.5 text-[10px] font-semibold uppercase tracking-tight">
            {t("megaMenu.topSeller", "Top Vendas")}
          </Badge>
        )}
      </Link>
    );
  };

  const COL3_SLUGS = type === "equipment"
    ? new Set(["prensas", "calandras", "textil", "equipamentos-textil", "outros-equipamentos"])
    : new Set(["textil", "pecas-acessorios"]);
  const col2 = secondaryGroup.filter((c) => !COL3_SLUGS.has(c.slug));
  const col3 = secondaryGroup.filter((c) => COL3_SLUGS.has(c.slug));
  const col3Title = type === "equipment"
    ? t("megaMenu.groupPresses", "Prensas & Outros")
    : t("megaMenu.groupAccessories", "Acessórios & Têxtil");
  const columns: { title: string; items: Category[]; accent: Accent }[] = [
    { title: primaryTitle, items: primaryGroup, accent: "primary" as Accent },
    { title: secondaryTitle, items: col2, accent: "finishing" as Accent },
    { title: col3Title, items: col3, accent: "finishing" as Accent },
  ].filter((c) => c.items.length > 0);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "inline-flex h-10 items-center justify-center rounded-md px-2 xl:px-3 py-2 text-sm font-medium",
            "bg-transparent text-foreground transition-colors",
            "hover:text-primary hover:bg-accent",
            "focus:outline-none focus:ring-2 focus:ring-primary/20",
            open && "bg-accent text-primary"
          )}
        >
          <span className="text-sm">{label}</span>
          <ChevronDown
            className={cn("ml-1 h-3 w-3 transition-transform duration-200", open && "rotate-180")}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[min(860px,calc(100vw-2rem))] p-0 border-border overflow-hidden rounded-xl shadow-2xl"
        align="start"
        sideOffset={8}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-background">
          <div>
            <h3 className="text-base font-bold text-foreground uppercase tracking-wider">
              {headerTitle}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">{headerSubtitle}</p>
          </div>
          <Link
            to={basePath}
            onClick={handleLinkClick}
            className="text-sm font-semibold text-primary hover:underline flex items-center gap-1.5 whitespace-nowrap"
          >
            {t("megaMenu.viewFullCatalog", "Ver catálogo completo")}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="grid grid-cols-3 gap-6 px-6 py-5 bg-background">
          {columns.map((col) => (
            <div key={col.title} className="space-y-2">
              <div className="flex items-center gap-2 px-2.5">
                <span className={cn("h-1.5 w-1.5 rounded-full", ACCENT_STYLES[col.accent].dot)} />
                <h4 className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">{col.title}</h4>
              </div>
              <div className="space-y-0.5">{col.items.map(renderItem(col.accent))}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-6 py-3 border-t border-border bg-muted/40 text-sm">
          <span className="text-muted-foreground">{t("megaMenu.techQuestionDesc", "Fale com um especialista B2B.")}</span>
          <Link to="/contacto" onClick={handleLinkClick} className="font-semibold text-primary hover:underline">
            {t("megaMenu.scheduleDemo", "Agendar Demo")}
          </Link>
          <span className="text-muted-foreground hidden lg:inline">{t("megaMenu.showroomDesc", "Agende uma visita e veja ao vivo.")}</span>
          <Link to="/assistencia-tecnica" onClick={handleLinkClick} className="ml-auto flex items-center gap-1.5 text-muted-foreground hover:text-primary">
            <HeadphonesIcon className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-tight">{t("megaMenu.supportCenter", "Centro de Suporte")}</span>
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
